import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Empty } from 'antd';
import cn from 'classnames';
import FlatsGrid from '../../../../../../components/ui/FlatsGrid/FlatsGrid';
import IndicatorsRow from '../../../../../../components/ui/IndicatorsRow/IndicatorsRow';
import ExpandedContent from './ExpandedContent';
import { setEntrancesInView } from '../../../../../../store/actions/housingStockActions';
import {
  acceptanceStatusesColorsMap,
  addressStagesColorsMap,
} from '../../../../../../constants/stagesColorMaps';
import { requestInFlatIndicators } from '../../../../../../constants/requestInFlatIndicators';
import { useStagesQuery } from '../../../../../../hooks/useStageLifeCircleQuery';
import {
  selectGridData,
  selectGridPreview,
  selectHasGridPreviewData,
  selectUserInfo,
} from '../../../../lib/storeSelectors';
import AddInfoBlock from '../../../../../../components/ui/AddInfoBlock/AddInfoBlock';
import { useModalActions } from '../../providers/modals';
import { texts } from '../../../../../../constants/texts';
import styles from './ChessView.module.scss';
import { useAcpentanceQuery } from '../../../../../../hooks/useAccpentanceQuery';
import { chessViewMode } from '../../../../../../constants/chessViewMode';
import FlatsChessViewTabs from '../../../../../../components/ui/FlatsChessViewTabs/FlatsChessViewTabs';
import AcceptanceStatusesCountView from '../AcceptanceStatusesCountView/AcceptanceStatusesCountView';
import StagesRowSelect from '../StagesRowSelect/StagesRowSelect';
import {
  reqestForFlat,
  requestInFlatPeriodIndicators,
} from '../../../../../../constants/reqestForFlat';
import useBorderColorForStages from '../../../../../../hooks/useBorderColorForStages';
import DateExcess from '../DateExcess/DateExcess';
import LegendsForApplicationMonitoring from '../LegendsForApplicationMonitoring/LegendsForApplicationMonitoring';
import FlatChessViewRequestTabs from '../../../../../../components/ui/FlatChessViewRequestTabs/FlatChessViewRequestTabs';

const PreviewFlatsGrid = () => {
  const { data } = useSelector(selectGridPreview);
  const rootRef = useRef();

  return (
    <div
      ref={rootRef}
      className={cn(styles.wrapper, styles.wrapper_preview)}
    >
      {data.map((item) => (
        <FlatsGrid
          key={item.name}
          className={styles.card}
          setCurrentEntry={null}
          data={item}
          onFlatClick={() => {}}
          parentContainer={rootRef}
          colSize="60px"
          isPreview={true}
        />
      ))}
    </div>
  );
};

const AddInfo = () => {
  const { showFlatsGridModal } = useModalActions();

  return (
    <AddInfoBlock
      onAddBtnClick={showFlatsGridModal}
      text={texts.chooseFlatsGridCreationMethod}
    />
  );
};

const EmptyFlatsGrid = () => {
  const { CanCreateFlatGrid } = useSelector(selectUserInfo);

  return CanCreateFlatGrid ? (
    <AddInfo />
  ) : (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
  );
};

const emptyArr = [];

const select = (data) => {
  const stages = data?.stagesAddressLifeCycle;

  return stages?.length
    ? stages
        .map((stage) => ({
          id: stage.id,
          value: stage.name,
          bg: addressStagesColorsMap.get(stage.id) || 'transparent',
        }))
        .sort((a, b) => a.id - b.id)
    : emptyArr;
};

const selectAcceptanceStatuses = (data) => {
  const statuses = data?.acceptanceStatuses;

  return statuses?.length
    ? statuses
        .map((stage) => ({
          id: stage.id,
          value: stage.name,
          bg: acceptanceStatusesColorsMap.get(stage.id) || 'transparent',
        }))
        .sort((a, b) => a.id - b.id)
    : emptyArr;
};

const ContentFlatsGrid = ({ gridData }) => {
  const [activeFlat, setActiveFlat] = useState(null);
  const [currentEntry, setCurrentEntry] = useState(null);
  const [parentContainer, setParentContainer] = useState(null);

  const dispatch = useDispatch();

  const chessViewColorMode = useSelector(
    (state) => state.housingStockStore.chessViewColorMode
  );

  const { data: stages = [] } = useStagesQuery({ staleTime: Infinity, select });
  const { data: acceptanceStatuses } = useAcpentanceQuery({
    staleTime: Infinity,
    select: selectAcceptanceStatuses,
  });

  const indicatorsBorderColorForStages = useBorderColorForStages();

  const scrollToCurrentEntry = useCallback(
    (el) => {
      setParentContainer(el);
      if (el && currentEntry) {
        el.scrollTo({
          left: currentEntry.offsetLeft - 10,
          behavior: 'smooth',
        });
      }
    },
    [currentEntry]
  );

  const scrollToActiveFlat = useCallback(
    (el) => {
      if (el && activeFlat) {
        window.scrollTo({
          top: el.offsetTop,
          behavior: 'smooth',
        });
      }
    },
    [activeFlat]
  );

  const handleFlatClick = (data) => {
    setActiveFlat(activeFlat?.addressId === data.addressId ? null : data);
  };

  useEffect(() => {
    return () => dispatch(setEntrancesInView([]));
  }, [dispatch]);

  useEffect(() => {
    let newActiveFlat = null;

    if (activeFlat) {
      newActiveFlat = gridData
        .reduce((acc, entrance) => acc.concat(entrance.flats), [])
        .find((flat) => flat.addressId === activeFlat.addressId);
    }

    setActiveFlat(newActiveFlat);
  }, [gridData, activeFlat]);

  const viewComponents = {
    [chessViewMode.stages]: (
      <>
        <IndicatorsRow indicators={requestInFlatIndicators} />
        <IndicatorsRow
          indicators={stages || emptyArr}
          squareIndicator
        />
      </>
    ),
    [chessViewMode.acceptanceStatuses]: (
      <IndicatorsRow
        indicators={acceptanceStatuses || emptyArr}
        squareIndicator
      />
    ),
    [chessViewMode.applicationMonitoring]: (
      <>
        {/* <IndicatorsRow indicators={requestInFlatPeriodIndicators} /> */}
        <FlatChessViewRequestTabs />
        <IndicatorsRow
          indicators={reqestForFlat || emptyArr}
          squareIndicator
        />
        {!!indicatorsBorderColorForStages?.length && (
          <IndicatorsRow
            indicators={indicatorsBorderColorForStages || emptyArr}
            isBorder
            squareIndicator
          />
        )}
      </>
    ),
  };

  return (
    <>
      <div className={styles.header}>
        <div className={styles.actionsRow}>
          {chessViewColorMode === chessViewMode.applicationMonitoring && (
            <>
              <DateExcess />
              <StagesRowSelect stages={stages} />
            </>
          )}
          <FlatsChessViewTabs />
        </div>
        <div
          className={styles.indicatorsRow}
          style={{
            justifyContent:
              chessViewColorMode === chessViewMode.stages
                ? 'space-between'
                : 'end',
          }}
        >
          {viewComponents[chessViewColorMode]}
        </div>
      </div>

      <div
        ref={scrollToCurrentEntry}
        className={styles.root}
      >
        <div className={styles.wrapper}>
          {gridData.map((item) => (
            <FlatsGrid
              key={item.name}
              className={styles.card}
              setCurrentEntry={setCurrentEntry}
              data={item}
              onFlatClick={handleFlatClick}
              parentContainer={parentContainer}
              colSize="60px"
            />
          ))}
        </div>
      </div>
      {chessViewColorMode === chessViewMode.acceptanceStatuses && (
        <AcceptanceStatusesCountView
          acceptanceStatuses={acceptanceStatuses}
          gridData={gridData}
        />
      )}

      {chessViewColorMode === chessViewMode.applicationMonitoring && (
        <LegendsForApplicationMonitoring gridData={gridData} />
      )}

      <div ref={scrollToActiveFlat}>
        {activeFlat && <ExpandedContent flatData={activeFlat} />}
      </div>
    </>
  );
};

const MainFlatsGrid = () => {
  const gridData = useSelector(selectGridData);

  return gridData?.length ? (
    <ContentFlatsGrid gridData={gridData} />
  ) : (
    <EmptyFlatsGrid />
  );
};

const ChessView = () => {
  const hasGridPreviewData = useSelector(selectHasGridPreviewData);

  return hasGridPreviewData ? <PreviewFlatsGrid /> : <MainFlatsGrid />;
};

export default ChessView;
